import { useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isHTTPError } from 'ky';
import type {
  AddOutsidePatternProcedureRequest,
  ProcedureWorkLineDto,
  SaveProcedureDraftRequest,
} from '@ctms/contracts';
import { AlertCircle, Check, Plus, Trash2, X } from 'lucide-react';

import { getApiErrorMessage } from '../../../api/client.js';
import { visitsApi } from '../visits.api.js';
import { visitQueryKeys } from '../visits.query-keys.js';

export interface ProcedureRegistrationProps {
  /** The Domain 3 visit whose procedure list is registered (REQ-VIS-045). */
  patientVisitId: number;
  /** Active trial of the cockpit — the source list of "add outside the pattern" (REQ-VIS-040). */
  trialId: number | null;
  /** Short label of the visit for the header, e.g. `V12` (presentation only). */
  visitLabel?: string;
  /** `visits:read` — without it the panel stays hidden (same rule as the day plan). */
  canRead: boolean;
  /** `visits:write` — without it the list is read-only. */
  canWrite: boolean;
  /** Called after the working list was persisted (REQ-VIS-045 step 6). */
  onSaved?: () => void;
}

/** Local draft overrides of ONE line, keyed by its position in the rendered list. */
interface LineOverride {
  readonly done: boolean;
  readonly comment: string | null;
}

function queryRetry(count: number, err: unknown): boolean {
  return isHTTPError(err) ? false : count < 3;
}

/**
 * Elements of the procedure registration (REQ-VIS-043…REQ-VIS-050): the working
 * list of one visit — the saved executions joined with the visit pattern (the
 * server composes it, REQ-VIS-045) — with a tick per position, a delete for
 * positions added outside the pattern (REQ-VIS-048) and the dialog that adds
 * one from the trial catalogue (REQ-VIS-050).
 *
 * Two write paths, deliberately:
 * - a position that ALREADY has a record is ticked in place through
 *   `PATCH procedure-executions/:id` (REQ-VIS-044: the procedure date is
 *   computed by the server from the visit's own dates — the client never
 *   dictates it);
 * - a pattern position that has NO record yet cannot be addressed by id, so it
 *   is materialized by the draft save (REQ-VIS-045 step 6) together with the
 *   local ticks and comments.
 *
 * Editing the procedure DATE and the COMMENT of a saved position by hand is
 * OUT of this atom (`REQ-VIS-046`, separate endpoint): the existing comment is
 * shown and passed through on save, so a save never drops it.
 */
export function ProcedureRegistration({
  patientVisitId,
  trialId,
  visitLabel,
  canRead,
  canWrite,
  onSaved,
}: ProcedureRegistrationProps): JSX.Element {
  const queryClient = useQueryClient();
  const [overrides, setOverrides] = useState<Record<number, LineOverride>>({});
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const workListQuery = useQuery({
    queryKey: visitQueryKeys.procedureWorkList(patientVisitId),
    queryFn: () => visitsApi.getProcedureWorkList(patientVisitId),
    enabled: canRead,
    // The list is operational, live data: a stale tick misleads the coordinator.
    staleTime: 0,
    retry: queryRetry,
  });

  const catalogueQuery = useQuery({
    queryKey: visitQueryKeys.procedureCatalogue(trialId ?? 0),
    queryFn: () => visitsApi.listProcedureCatalogue(trialId ?? 0),
    enabled: canRead && isAddOpen && trialId !== null,
    retry: queryRetry,
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: visitQueryKeys.procedureWorkList(patientVisitId),
    });
  };

  const tickMutation = useMutation({
    mutationFn: ({ id, done }: { id: number; done: boolean }) =>
      visitsApi.setProcedureExecutionDone(id, done),
    onSuccess: refresh,
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => visitsApi.removeProcedureExecution(id),
    onSuccess: refresh,
  });

  const addMutation = useMutation({
    mutationFn: (request: AddOutsidePatternProcedureRequest) =>
      visitsApi.addOutsidePatternProcedure(request),
    onSuccess: async () => {
      setIsAddOpen(false);
      await refresh();
    },
  });

  const saveMutation = useMutation({
    mutationFn: (request: SaveProcedureDraftRequest) =>
      visitsApi.saveProcedureDraft(patientVisitId, request),
    onSuccess: async (lines) => {
      setOverrides({});
      // One source of truth: the server returns the canonical list after the save.
      queryClient.setQueryData(visitQueryKeys.procedureWorkList(patientVisitId), lines);
      setErrorMessage(null);
      onSaved?.();
    },
    onError: async (err: unknown) => {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się zapisać listy procedur.'));
    },
  });

  const lines = workListQuery.data ?? [];

  /**
   * The list as the user sees it: the server's canonical lines with the local
   * ticks and comments of positions that have no record yet overlaid on top.
   * A saved line is read straight from the server, so an instant tick can never
   * be double-guessed by a stale local copy.
   */
  const renderedLines = useMemo(
    () =>
      lines.map((line, index) => {
        const override = overrides[index];
        if (!override || line.key !== null) {
          return line;
        }
        return { ...line, done: override.done, comment: override.comment };
      }),
    [lines, overrides],
  );

  /**
   * REQ-VIS-045 step 6 + REQ-VIS-049: the WHOLE list goes back on save. Every
   * line keeps its catalogue entry, its record id (when it has one) and its
   * existing comment, so a save never drops a comment nor deletes a position.
   * The server matches each line against the canonical list — a position that
   * is not saved yet is materialized, a saved one is refreshed in place.
   */
  const toDraftRequest = (): SaveProcedureDraftRequest => ({
    lines: renderedLines.map((line) =>
      line.key !== null
        ? {
            // An already-saved line is addressed by its own id: it is refreshed
            // in place and its tick already travelled through the PATCH endpoint.
            key: line.key,
            catalogId: line.catalogId,
            ...(line.comment !== null ? { comment: line.comment } : {}),
          }
        : {
            // A position with no record yet is materialized by this save, so it
            // carries the tick of the draft (REQ-VIS-045 step 6).
            catalogId: line.catalogId,
            done: line.done,
            ...(line.comment !== null ? { comment: line.comment } : {}),
          },
    ),
  });

  const toggleLine = (index: number, line: ProcedureWorkLineDto, done: boolean): void => {
    if (!canWrite) {
      return;
    }
    setErrorMessage(null);
    if (line.key === null) {
      // No record yet — the tick travels with the draft save (REQ-VIS-045 step 6).
      setOverrides((current) => ({
        ...current,
        [index]: { done, comment: line.comment },
      }));
      return;
    }
    // Already saved: tick in place (REQ-VIS-044/049) — the server keeps the row
    // and computes the procedure date.
    tickMutation.mutate(
      { id: Number(line.key), done },
      {
        onError: async (err: unknown) => {
          setErrorMessage(
            await getApiErrorMessage(err, 'Nie udało się zapisać odhaczenia procedury.'),
          );
        },
      },
    );
  };

  const removeLine = (line: ProcedureWorkLineDto): void => {
    // REQ-VIS-048: only a line added outside the pattern is removable; the
    // server refuses a pattern position with a 409 and the UI never offers it.
    if (!canWrite || !line.removable || line.key === null) {
      return;
    }
    setErrorMessage(null);
    removeMutation.mutate(Number(line.key), {
      onError: async (err: unknown) => {
        setErrorMessage(await getApiErrorMessage(err, 'Nie udało się usunąć procedury.'));
      },
    });
  };

  const retry = (): void => {
    void workListQuery.refetch();
  };

  if (!canRead) {
    return (
      <section role="alert" className="rounded-lg border border-slate-800 bg-slate-900/60 p-8">
        <h2 className="text-base font-semibold text-slate-100">
          Brak dostępu do rejestracji procedur
        </h2>
        <p className="mt-2 text-xs text-slate-400">
          Nie możesz zmieniać procedur dodatkowych w tym ośrodku. Jeżeli uważasz, że to błąd,
          skontaktuj się z administratorem ośrodka.
        </p>
      </section>
    );
  }

  const isBusy =
    saveMutation.isPending || tickMutation.isPending || removeMutation.isPending || addMutation.isPending;

  return (
    <section
      aria-label="Rejestracja procedur dodatkowych"
      className="rounded-lg border border-slate-800 bg-slate-900/80"
    >
      <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-100">
            Procedury dodatkowe{visitLabel ? ` — ${visitLabel}` : ''}
          </h2>
          <p className="text-[11px] text-slate-400">
            Odznaczone pozycje wzorca zostają jako niewykonane; usuwa się tylko procedury spoza wzorca.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setIsAddOpen(true)}
            disabled={!canWrite || trialId === null || isBusy}
            className="flex items-center gap-1.5 rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" />
            Dodaj spoza wzorca
          </button>
          <button
            type="button"
            onClick={() => setOverrides({})}
            disabled={Object.keys(overrides).length === 0 || isBusy}
            className="flex items-center gap-1.5 rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
            Anuluj zmiany robocze
          </button>
          <button
            type="button"
            onClick={() => saveMutation.mutate(toDraftRequest())}
            disabled={!canWrite || isBusy || lines.length === 0}
            className="flex items-center gap-1.5 rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
            {saveMutation.isPending ? 'Zapisywanie…' : 'Zapisz listę procedur'}
          </button>
        </div>
      </div>

      {errorMessage && (
        <div
          role="alert"
          className="mx-4 mt-3 flex items-center gap-2.5 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-xs text-rose-300"
        >
          <AlertCircle className="h-4 w-4 shrink-0 text-rose-400" />
          <span>{errorMessage}</span>
        </div>
      )}

      {!canWrite && (
        <p className="mx-4 mt-3 rounded-md border border-amber-800/60 bg-amber-950/30 p-3 text-xs text-amber-300">
          Nie masz uprawnienia do zapisu procedur — lista jest tylko do wglądu.
        </p>
      )}

      <div className="p-4">
        {workListQuery.isLoading ? (
          <p className="py-8 text-center text-xs text-slate-400">Przygotowanie listy procedur…</p>
        ) : workListQuery.isError ? (
          <section role="alert" className="rounded-lg border border-rose-800/70 bg-rose-950/40 p-4">
            <p className="text-xs text-rose-200">Nie udało się przygotować listy procedur.</p>
            <button
              type="button"
              onClick={retry}
              className="mt-2 rounded-md border border-rose-700 px-3 py-1.5 text-xs text-rose-200"
            >
              Spróbuj ponownie
            </button>
          </section>
        ) : renderedLines.length === 0 ? (
          <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-6 text-center">
            <p className="text-xs text-slate-400">
              Dla tej wizyty nie zdefiniowano procedur dodatkowych w badaniu.
            </p>
            <button
              type="button"
              onClick={() => setIsAddOpen(true)}
              disabled={!canWrite || trialId === null}
              className="mt-3 rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Dodaj spoza wzorca
            </button>
          </div>
        ) : (
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-950/60">
                <th scope="col" className="px-3 py-2 text-slate-400">
                  Wykonana
                </th>
                <th scope="col" className="px-3 py-2 text-slate-400">
                  Procedura
                </th>
                <th scope="col" className="px-3 py-2 text-slate-400">
                  Data
                </th>
                <th scope="col" className="px-3 py-2 text-slate-400">
                  Komentarz
                </th>
                <th scope="col" className="px-3 py-2 text-right text-slate-400">
                  Akcje
                </th>
              </tr>
            </thead>
            <tbody>
              {renderedLines.map((line, index) => (
                <tr
                  key={`${line.procedureKey}-${index}`}
                  className="border-b border-slate-800/80 align-middle"
                >
                  <td className="px-3 py-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={line.done}
                        disabled={!canWrite || isBusy}
                        aria-label={`Wykonana: ${line.name}`}
                        onChange={(event) => toggleLine(index, line, event.target.checked)}
                        className="h-4 w-4 rounded border-slate-600 bg-slate-950 accent-cyan-500"
                      />
                    </label>
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-slate-100">{line.name}</span>
                    {line.removable && (
                      <span className="ml-2 rounded border border-amber-800/70 bg-amber-950/40 px-1.5 py-0.5 text-[10px] text-amber-300">
                        spoza wzorca
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-300">{line.performedDate ?? '—'}</td>
                  <td className="px-3 py-2 text-slate-400">
                    {line.comment !== null && line.comment !== '' ? (
                      <span title={line.comment}>{line.comment}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {line.removable ? (
                      <button
                        type="button"
                        onClick={() => removeLine(line)}
                        disabled={!canWrite || isBusy || line.key === null}
                        aria-label={`Usuń procedurę spoza wzorca: ${line.name}`}
                        title="Usuń procedurę spoza wzorca"
                        className="inline-flex items-center gap-1 rounded border border-rose-800/70 px-2 py-1 text-[11px] text-rose-300 hover:bg-rose-950/40 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Trash2 className="h-3 w-3" />
                        Usuń
                      </button>
                    ) : (
                      <span className="text-[11px] text-slate-500">wzorzec</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {isAddOpen && (
        <AddOutsidePatternDialog
          procedures={catalogueQuery.data ?? []}
          isLoading={catalogueQuery.isLoading}
          isError={catalogueQuery.isError}
          isSubmitting={addMutation.isPending}
          onCancel={() => setIsAddOpen(false)}
          onConfirm={(catalogId, comment) => {
            setErrorMessage(null);
            addMutation.mutate(
              {
                patientVisitId,
                catalogId,
                ...(comment !== null && comment.trim() !== '' ? { comment: comment.trim() } : {}),
              },
              {
                onError: async (err: unknown) => {
                  setErrorMessage(
                    await getApiErrorMessage(err, 'Nie udało się dodać procedury.'),
                  );
                },
              },
            );
          }}
        />
      )}
    </section>
  );
}

interface AddOutsidePatternDialogProps {
  procedures: { id: number; name: string }[];
  isLoading: boolean;
  isError: boolean;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: (catalogId: number, comment: string | null) => void;
}

/**
 * REQ-VIS-050: adds a catalogue procedure OUTSIDE the visit pattern. The source
 * list is the trial's catalogue (REQ-VIS-040), not the visit pattern — that is
 * exactly what makes the added position removable (REQ-VIS-048).
 */
function AddOutsidePatternDialog({
  procedures,
  isLoading,
  isError,
  isSubmitting,
  onCancel,
  onConfirm,
}: AddOutsidePatternDialogProps): JSX.Element {
  const [catalogId, setCatalogId] = useState<string>('');
  const [comment, setComment] = useState<string>('');

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-outside-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-xs"
    >
      <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <h3 id="add-outside-title" className="text-base font-semibold text-slate-100">
          Dodaj procedurę z katalogu badania
        </h3>
        <p className="mt-1 text-xs text-slate-400">
          Pozycja dodana w ten sposób jest oznaczona jako spoza wzorca i można ją usunąć.
        </p>

        {isLoading ? (
          <p className="mt-4 text-xs text-slate-400">Ładowanie katalogu procedur…</p>
        ) : isError ? (
          <p role="alert" className="mt-4 text-xs text-rose-300">
            Nie udało się wczytać katalogu procedur badania.
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            <div>
              <label
                htmlFor="outside-procedure"
                className="block text-xs font-medium uppercase tracking-wider text-slate-300"
              >
                Procedura
              </label>
              <select
                id="outside-procedure"
                value={catalogId}
                disabled={isSubmitting}
                onChange={(event) => setCatalogId(event.target.value)}
                className="mt-1.5 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 disabled:opacity-50"
              >
                <option value="">Wybierz procedurę</option>
                {procedures.map((procedure) => (
                  <option key={procedure.id} value={String(procedure.id)}>
                    {procedure.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="outside-comment"
                className="block text-xs font-medium uppercase tracking-wider text-slate-300"
              >
                Komentarz (opcjonalnie)
              </label>
              <input
                id="outside-comment"
                type="text"
                value={comment}
                disabled={isSubmitting}
                onChange={(event) => setComment(event.target.value)}
                className="mt-1.5 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 disabled:opacity-50"
              />
            </div>
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2 border-t border-slate-800 pt-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={() => onConfirm(Number(catalogId), comment)}
            disabled={isSubmitting || catalogId === ''}
            className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? 'Dodawanie…' : 'Dodaj'}
          </button>
        </div>
      </div>
    </div>
  );
}